import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { CompetitionDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface CompetitionFormProps {
  initialValues: CompetitionDTO;
  onSubmit: (data: CompetitionDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
} as const;

const CompetitionForm: React.FC<CompetitionFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CompetitionDTO>(initialValues);

  const validationRules: ValidationRules = {
    name: [{ required: true, message: 'Name is required' }],
    startDate: [
      { required: true, message: 'Start Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Start Date must be a valid date',
      },
    ],
    endDate: [
      { required: true, message: 'End Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'End Date must be a valid date',
      },
    ],
    amount: [
      { required: true, message: 'Amount is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Amount must be a positive number',
      },
    ],
    registrationOpenDate: [
      { required: true, message: 'Registration Open Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Registration Open Date must be a valid date',
      },
    ],
    registrationCloseDate: [
      { required: true, message: 'Registration Close Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Registration Close Date must be a valid date',
      },
    ],
    targetDownloadOpenDate: [
      { required: true, message: 'Target Download Open Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Target Download Open Date must be a valid date',
      },
    ],
    targetDownloadCloseDate: [
      { required: true, message: 'Target Download Close Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Target Download Close Date must be a valid date',
      },
    ],
    showBannerDate: [
      { required: true, message: 'Show Banner Date is required' },
      {
        custom: (value) => !isNaN(Date.parse(value)),
        message: 'Show Banner Date must be a valid date',
      },
    ],
  };

  const { errors, validate } =
    useFormValidation<CompetitionDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Name"
          required={true}
          error={errors.name}
        />
        <FormField
          id="amount"
          label="Amount"
          type="number"
          value={formData.amount || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Amount"
          required={true}
          error={errors.amount}
        />
        <FormField
          id="registrationOpenDate"
          label="Registration Open Date"
          type="date"
          value={formData.registrationOpenDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Registration Open Date"
          required={true}
          error={errors.registrationOpenDate}
        />
        <FormField
          id="registrationCloseDate"
          label="Registration Close Date"
          type="date"
          value={formData.registrationCloseDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Registration Close Date"
          required={true}
          error={errors.registrationCloseDate}
        />
        <FormField
          id="targetDownloadOpenDate"
          label="Target Download Open Date"
          type="date"
          value={formData.targetDownloadOpenDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Target Download Open Date"
          required={true}
          error={errors.targetDownloadOpenDate}
        />
        <FormField
          id="targetDownloadCloseDate"
          label="Target Download Close Date"
          type="date"
          value={formData.targetDownloadCloseDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Target Download Close Date"
          required={true}
          error={errors.targetDownloadCloseDate}
        />
        <FormField
          id="startDate"
          label="Start Date"
          type="date"
          value={formData.startDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Start Date"
          required={true}
          error={errors.startDate}
        />
        <FormField
          id="endDate"
          label="End Date"
          type="date"
          value={formData.endDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter End Date"
          required={true}
          error={errors.endDate}
        />
        <FormField
          id="rulesTargetUrl"
          label="Rules Target Url"
          type="text"
          value={formData.rulesTargetUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Rules Target Url"
          required={false}
          error={errors.rulesTargetUrl}
          helper='Link provided must start with https://'
        />
        <FormField
          id="showBannerDate"
          label="Show Banner Date"
          type="date"
          value={formData.showBannerDate || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Show Banner Date"
          required={true}
          error={errors.showBannerDate}
        />
      </FormSection>
    </Form>
  );
};

export default CompetitionForm;
